import { Fragment, useState } from 'react';

import PropTypes from 'prop-types';
import { AdminInvoiceCreateError } from 'utils/constants/admin';
import { VIEW_DATE_WITH_FULL_MONTH_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import Button, { ButtonIconPosition, ButtonKind } from 'bloko/blocks/button';
import Icon, { IconAppearance, IconColor, IconKind, IconScale, IconView } from 'bloko/blocks/icon';
import InputText, { InputType } from 'bloko/blocks/inputText';
import Link from 'bloko/blocks/link';
import Modal, { ModalContent, ModalHeader, ModalTitle } from 'bloko/blocks/modal';
import { Option } from 'bloko/blocks/select';
import SelectWithPlaceholder from 'bloko/blocks/select/SelectWithPlaceholder';
import Text from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';

import useTrls from 'hooks/translation/useTrls';

import ClickmeButtonsWrapper from 'components/ClickmeButtonsWrapper';
import ClickmeFormItem from 'components/ClickmeFormItem';
import KMNumber from 'components/KMNumber';

import makeInvoice from 'components/Admin/Payers/CreateInvoiceModal/makeInvoice';

import { setInvoiceAccountId, setInvoiceAmount, setInvoiceInfo, setInvoicePayer } from 'store/models/payers';
import { useDispatch, useSelector } from 'store/utils';

import 'components/Admin/Payers/CreateInvoiceModal/CreateInvoiceModal.less';

const CreateInvoiceModal = ({ visible, setVisible }) => {
    const { invoicePayer, invoiceAmount, invoiceInfo, invoiceAccountId } = useSelector((state) => state.payers);
    const { payerId, userId, userName, email, accounts } = invoicePayer;
    const { id, number, createdAt } = invoiceInfo;

    const [isLoad, setLoad] = useState(false);
    const [errors, setErrors] = useState({});
    const [isCreatedInvoice, setCreatedInvoice] = useState(false);

    const dispatch = useDispatch();

    const trl = useTrls();

    const isMultiCabinet = accounts && (accounts.length > 1 || (accounts.length === 1 && !accounts[0].main));

    const errorText = {
        [AdminInvoiceCreateError.EmptyAmount]: trl('admin.Payers.InvoiceCreate.error'),
        [AdminInvoiceCreateError.EmptyAccountId]: trl('admin.Payers.InvoiceCreate.error.accountId.empty'),
        [AdminInvoiceCreateError.UnnecessaryAccountId]: trl('admin.Payers.InvoiceCreate.error.accountId.unnecessary'),
    };

    return (
        <Modal
            visible={visible}
            onClose={() => {
                setVisible(false);
                dispatch([setInvoicePayer({}), setInvoiceAmount(''), setInvoiceAccountId(''), setInvoiceInfo({})]);
                setCreatedInvoice(false);
                setErrors({});
            }}
        >
            <ModalHeader>
                <ModalTitle>
                    {isCreatedInvoice
                        ? trl('InvoiceCreateBankResult.pageTitle')
                        : trl('admin.Payers.CreateInvoice.title')}
                </ModalTitle>
            </ModalHeader>
            <ModalContent>
                <div className="modal-create-invoice">
                    {isCreatedInvoice ? (
                        <Fragment>
                            <Text strong>
                                {trl('InvoiceCreateBankResult.Invoice.number')}
                                <KMNumber userId={userId} number={number} />
                                {trl('InvoiceCreateBankResult.Invoice.from')}
                                {formatDate(createdAt, VIEW_DATE_WITH_FULL_MONTH_FORMAT)}
                            </Text>
                            <VSpacing base={6} />
                            <Link href={`/api/v1/render/invoice/${id}`} download>
                                {trl('Invoice.bill.pdf')}
                            </Link>{' '}
                            <Link href={`/api/v1/render/invoice/${id}?format=DOC`} download>
                                {trl('Invoice.bill.doc')}
                            </Link>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <Text strong>
                                {trl('admin.Payers.Payer.payerId')}
                                {payerId}
                            </Text>
                            <VSpacing base={2} />
                            <Text strong>
                                {trl('admin.Users.User.id')}
                                {userId}
                            </Text>
                            <VSpacing base={2} />
                            <Text strong>
                                {trl('admin.Users.User.name')}
                                {userName}
                            </Text>
                            <VSpacing base={2} />
                            <Text strong>
                                {trl('admin.Users.User.email')}
                                {email}
                            </Text>
                            <VSpacing base={6} />
                            <ClickmeFormItem
                                input={
                                    <InputText
                                        value={invoiceAmount}
                                        type={InputType.Number}
                                        onChange={(value) => dispatch(setInvoiceAmount(value))}
                                        placeholder={trl('InvoiceCreateBank.InvoiceForm.Amount.placeholder')}
                                        invalid={errors.amount}
                                    />
                                }
                                legend={trl('InvoiceCreateBank.InvoiceForm.Amount.legend')}
                                isRequired
                                error={errorText[errors.amount]}
                            />
                            {isMultiCabinet && (
                                <ClickmeFormItem
                                    input={
                                        <SelectWithPlaceholder
                                            value={invoiceAccountId}
                                            onChange={(event) => dispatch(setInvoiceAccountId(event.target.value))}
                                            placeholder={trl('admin.Payers.InvoiceCreate.accountId.placeholder')}
                                            data-qa="accounts-select"
                                        >
                                            {accounts.map(({ id, title }) => (
                                                <Option key={id} value={id} data-qa="accounts-option">
                                                    {trl('admin.accounts.modal.link', [id, title])}
                                                </Option>
                                            ))}
                                        </SelectWithPlaceholder>
                                    }
                                    legend={trl('admin.Payers.InvoiceCreate.accountId.legend')}
                                    isRequired
                                    error={errorText[errors.accountId]}
                                />
                            )}
                            <ClickmeButtonsWrapper>
                                <Button
                                    kind={ButtonKind.Success}
                                    icon={
                                        <Icon
                                            view={IconView.Checkmark}
                                            initial={IconColor.White}
                                            kind={IconKind.Single}
                                            appearance={IconAppearance.Outlined}
                                            scale={IconScale.Medium}
                                        />
                                    }
                                    iconPosition={ButtonIconPosition.Right}
                                    onClick={() => dispatch(makeInvoice(setLoad, setCreatedInvoice, setErrors))}
                                    disabled={isLoad}
                                    data-qa="payer-create-invoice-finish"
                                >
                                    {trl('InvoiceCreateBank.InvoiceForm.save')}
                                </Button>
                            </ClickmeButtonsWrapper>
                        </Fragment>
                    )}
                </div>
            </ModalContent>
        </Modal>
    );
};

CreateInvoiceModal.propTypes = {
    visible: PropTypes.bool,
    setVisible: PropTypes.func,
};

export default CreateInvoiceModal;
